import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useGroup } from '../../contexts/GroupContext';
import NucleusPage from '../../components/nucleus/NucleusPage';
import { ZoneMark } from '../../components/nucleus/zones';
import { pathFor } from '../../nucleus/map';
import { CONCEPTS, anchorOf } from '../../nucleus/concepts';
import ConceptDiagram from './diagrams';
import './HelpPage.css';

// HELP — the concepts ofSelf runs on (nucleus/concepts.js), one at a time. On the
// left, the five concepts, the open one's parts beneath it. On the right, that one
// concept: what it is, a diagram of just that idea, and its parts; pressing a part
// lights it in the diagram. A link (#realms, #realms-schemas) opens a concept, or one
// of its parts, and the search lands the same way.

// "#realms-schemas" → { concept: 'realms', part: 'schemas' }. A part that was
// called Actuators is Plugins now: an old link still lands on it.
function read(hash) {
    const id = decodeURIComponent((hash || '').replace(/^#/, '')).replace(/-actuators$/, '-plugins');
    const c = CONCEPTS.find((x) => id === x.id || id.startsWith(`${x.id}-`));
    if (!c) return { concept: CONCEPTS[0].id, part: null };
    const p = c.parts.find((x) => anchorOf(c, x) === id);
    return { concept: c.id, part: p ? p.id : null };
}

export default function HelpPage() {
    const navigate = useNavigate();
    const { hash } = useLocation();
    const { activeGroupId } = useGroup();
    const [at, setAt] = useState(() => read(hash));
    useEffect(() => { if (hash) setAt(read(hash)); }, [hash]);

    const open = (concept, part = null) => {
        setAt({ concept, part });
        const c = CONCEPTS.find((x) => x.id === concept);
        const id = c && part ? anchorOf(c, c.parts.find((p) => p.id === part)) : concept;
        window.history.replaceState(null, '', `#${id}`);
    };
    const i = CONCEPTS.findIndex((c) => c.id === at.concept);
    const c = CONCEPTS[i];
    const part = c?.parts.find((p) => p.id === at.part) || null;

    return (
        <NucleusPage title="Help" bodyClassName="help-body">
            <div className="help">
                <nav className="help-nav" aria-label="Concepts">
                    {CONCEPTS.map((x) => (
                        <div key={x.id} data-zone={x.zone} className={`help-nav__group${x.id === at.concept ? ' is-open' : ''}`}>
                            <button type="button" className="help-nav__concept" aria-current={x.id === at.concept && !at.part ? 'true' : undefined} onClick={() => open(x.id)}>
                                <ZoneMark zone={x.zone} size={12} /> {x.word}
                            </button>
                            {x.id === at.concept && (
                                <ul className="help-nav__parts">
                                    {x.parts.map((p) => (
                                        <li key={p.id}>
                                            <button type="button" aria-current={p.id === at.part ? 'true' : undefined} onClick={() => open(x.id, p.id)}>{p.word}</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                    <p className="help-nav__support">Anything else: <a href="mailto:support@ofself.com">support@ofself.com</a></p>
                </nav>

                {c && (
                    <article className="help-stage" data-zone={c.zone} aria-label={c.word}>
                        <header className="help-stage__head">
                            <ZoneMark zone={c.zone} size={16} />
                            <h2>{c.word}</h2>
                            <button type="button" className="nx-link" onClick={() => navigate(pathFor(c.page.section, c.page.page, activeGroupId || null))}>{c.page.label}</button>
                        </header>
                        <p className="help-stage__line">{c.line}</p>

                        <figure className="help-figure">
                            <ConceptDiagram concept={c.id} focus={part?.id || null} />
                            <figcaption aria-live="polite">
                                {part ? <><b>{part.word}.</b> {part.line}</> : 'Press a part below to see it in the diagram.'}
                            </figcaption>
                        </figure>

                        <ol className="help-parts">
                            {c.parts.map((p) => (
                                <li key={p.id}>
                                    <button type="button" className={`help-part${p.id === at.part ? ' is-on' : ''}`} aria-pressed={p.id === at.part}
                                        onClick={() => open(c.id, p.id === at.part ? null : p.id)}>
                                        <span className="help-part__word">{p.word}</span>
                                        <span className="help-part__line">{p.line}</span>
                                    </button>
                                </li>
                            ))}
                        </ol>

                        <footer className="help-stage__foot">
                            {i > 0 ? <button type="button" className="nx-link" onClick={() => open(CONCEPTS[i - 1].id)}><ArrowLeft size={13} aria-hidden="true" /> {CONCEPTS[i - 1].word}</button> : <span />}
                            {i < CONCEPTS.length - 1 && <button type="button" className="nx-link" onClick={() => open(CONCEPTS[i + 1].id)}>{CONCEPTS[i + 1].word} <ArrowRight size={13} aria-hidden="true" /></button>}
                        </footer>
                    </article>
                )}
            </div>
        </NucleusPage>
    );
}
